import React, {useRef, useState} from "react";
import {Button, Col, Form, Input, message, Modal, Radio, Row, Upload} from "antd";
import {BellOutlined, UploadOutlined} from "@ant-design/icons";
import {useFetch} from "hooks/useFetch";
import {useStore} from "hooks/useStore";
import {AnnouncementPreview} from "./announcementPreview/AnnouncementPreview";
import {sanitizeWhatsappText} from "./announcementPreview/sanitizeWhatsappText";
import {sanitizeWhatsappPaste} from "./announcementPreview/sanitizeWhatsappPaste";

const TITLE_MAX_LENGTH = 100;
const MESSAGE_MAX_LENGTH = 1500;

export const SendAnnouncementWithExcelModal = ({visible, onClose, allowedChannels}) => {
    const stores = useStore();
    const [form] = Form.useForm();
    const [file, setFile] = useState(null);
    const formDataRef = useRef(null);
    // Kullanıcı sadece bir kanala yetkiliyse (ör. yalnızca whatsapp), o kanal varsayılan seçilir
    const defaultChannel = allowedChannels?.app ? "app" : "whatsapp";
    const initialPreviewState = {channel: defaultChannel, title: "", message: ""};
    const [preview, setPreview] = useState(initialPreviewState);

    const [loading, , , , refetch] = useFetch({
        fetchFn: () =>
            formDataRef.current.channel === "whatsapp"
                ? stores.courierStore.sendWhatsappAnnouncementWithExcel(
                      formDataRef.current.formData
                  )
                : stores.courierStore.sendAnnouncementWithExcel(formDataRef.current.formData),
        initialCall: false,
        callbackFunc: result => {
            if (result?.success) {
                form.resetFields();
                setFile(null);
                setPreview(initialPreviewState);
                onClose();
            }
        }
    });

    const handleValuesChange = (_, allValues) => {
        const isWhatsapp = allValues.channel === "whatsapp";
        setPreview({
            channel: allValues.channel,
            title: isWhatsapp ? sanitizeWhatsappText(allValues.title) : allValues.title,
            message: isWhatsapp ? sanitizeWhatsappText(allValues.message) : allValues.message
        });
    };

    // WhatsApp şablonu satır sonu kabul etmediği için Enter ile yeni satıra geçilemiyor
    const handleMessageKeyDown = event => {
        if (event.key === "Enter" && preview.channel === "whatsapp") {
            event.preventDefault();
        }
    };

    const handlePaste = (fieldName, maxLength) => event => {
        if (preview.channel !== "whatsapp") return;
        const nextValue = sanitizeWhatsappPaste(event, maxLength);
        if (nextValue === null) return;
        form.setFieldsValue({[fieldName]: nextValue});
        handleValuesChange(null, form.getFieldsValue());
    };

    const handleConfirm = async () => {
        if (!file) {
            message.error("Lütfen bir Excel dosyası seçiniz.");
            return;
        }
        try {
            const values = await form.validateFields();
            const isWhatsapp = values.channel === "whatsapp";
            const formData = new FormData();
            formData.append("file", file);
            formData.append("title", isWhatsapp ? sanitizeWhatsappText(values.title).trim() : values.title);
            formData.append(
                "body",
                isWhatsapp ? sanitizeWhatsappText(values.message).trim() : values.message
            );
            formDataRef.current = {channel: values.channel, formData};
            refetch();
        } catch (error) {
            // form validation hatası, refetch tetiklenmez
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setFile(null);
        setPreview(initialPreviewState);
        onClose();
    };

    return (
        <Modal
            visible={visible}
            title={
                <span>
                    <BellOutlined style={{marginRight: 8}} />
                    Excel ile Duyuru Gönder
                </span>
            }
            okText="Duyuruyu Gönder"
            cancelText="İptal"
            centered
            destroyOnClose
            width={900}
            confirmLoading={loading}
            onOk={handleConfirm}
            onCancel={handleCancel}
        >
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={initialPreviewState}
                        onValuesChange={handleValuesChange}
                    >
                        <Form.Item
                            name="channel"
                            label="Gönderim Kanalı"
                            rules={[{required: true, message: "Gönderim kanalı zorunludur"}]}
                        >
                            <Radio.Group>
                                {allowedChannels?.app && (
                                    <Radio value="app">Vigo Kurye uygulamasına bildirim</Radio>
                                )}
                                {allowedChannels?.whatsapp && (
                                    <Radio value="whatsapp">WhatsApp mesajı</Radio>
                                )}
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item label="Excel Dosyası" required>
                            <Upload
                                accept=".xlsx"
                                maxCount={1}
                                fileList={file ? [file] : []}
                                beforeUpload={selectedFile => {
                                    setFile(selectedFile);
                                    return false;
                                }}
                                onRemove={() => setFile(null)}
                            >
                                <Button icon={<UploadOutlined />}>Excel Dosyası Seç</Button>
                            </Upload>
                        </Form.Item>
                        <Form.Item
                            name="title"
                            label="Duyuru Başlığı"
                            rules={[{required: true, message: "Başlık zorunludur"}]}
                        >
                            <Input
                                placeholder="Duyuru başlığını giriniz"
                                maxLength={TITLE_MAX_LENGTH}
                                showCount
                                onPaste={handlePaste("title", TITLE_MAX_LENGTH)}
                            />
                        </Form.Item>
                        <Form.Item
                            name="message"
                            label="Duyuru Mesajı"
                            rules={[{required: true, message: "Mesaj zorunludur"}]}
                        >
                            <Input.TextArea
                                rows={4}
                                placeholder="Duyuru mesajını giriniz"
                                maxLength={MESSAGE_MAX_LENGTH}
                                showCount
                                onKeyDown={handleMessageKeyDown}
                                onPaste={handlePaste("message", MESSAGE_MAX_LENGTH)}
                            />
                        </Form.Item>
                    </Form>
                </Col>
                <Col xs={24} md={12}>
                    <AnnouncementPreview
                        channel={preview.channel}
                        title={preview.title}
                        message={preview.message}
                    />
                </Col>
            </Row>
        </Modal>
    );
};
